import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getEpicorInvoice } from '../../services/epicorApi';
import PartCell from '../orders/PartCell';
import {
  discountDisplay,
  fmt,
  fmtAmt,
  fmtDate,
  miscAmountDisplay,
  netExt,
  netUnitPrice,
} from '../orders/orderFormat';

const Corners = () => (
  <>
    <i className="oc-corner tl" aria-hidden="true" />
    <i className="oc-corner tr" aria-hidden="true" />
    <i className="oc-corner bl" aria-hidden="true" />
    <i className="oc-corner br" aria-hidden="true" />
  </>
);

const Stat = ({ value, label }) => (
  <div className="oc-stat oc-stat-static oc-blueprint">
    <Corners />
    <span className="oc-stat-value text">{value}</span>
    <span className="oc-stat-label">{label}</span>
  </div>
);

const InvoiceDetailPage = () => {
  const { company, invoiceNum } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('lines');

  const load = useCallback(async () => {
    try {
      setError(null);
      setData(await getEpicorInvoice(company, invoiceNum));
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  }, [company, invoiceNum]);

  useEffect(() => { load(); }, [load]);

  if (error) {
    return (
      <div className="oc-scope">
        <p className="oc-error">{error}</p>
        <Link to="/invoices">← Back to invoices</Link>
      </div>
    );
  }
  if (!data) {
    return <div className="oc-scope"><p>Loading…</p></div>;
  }

  const {
    invoice, customer, lines, misc, orders, payments,
    credit_notes: creditNotes, original_invoice: originalInvoice, totals,
  } = data;
  const isCreditNote = Boolean(invoice.credit_memo);

  const tabs = [
    { id: 'lines', label: `Lines (${lines.length})` },
    { id: 'charges', label: `Charges (${misc.length})` },
    { id: 'orders', label: `Orders (${orders.length})` },
    { id: 'payments', label: `Payments (${payments.length})` },
    { id: 'credits', label: `Credit notes (${creditNotes.length})` },
  ];

  return (
    <div className="customers-page oc-scope">
      <header className="oc-header">
        <div>
          <Link to="/invoices" className="oc-back">← Invoices</Link>
          <h1 className="oc-title">
            {company} {isCreditNote ? 'credit note' : 'invoice'} {invoiceNum}
            {totals?.orphan_header ? (
              <span className="oc-hint"> (header not yet synced)</span>
            ) : null}
          </h1>
          <p className="oc-blurb">
            {fmtDate(invoice.invoice_date)}
            {invoice.due_date ? ` · due ${fmtDate(invoice.due_date)}` : ''}
            {invoice.cust_num != null && (
              <>
                {' · '}
                <Link to={`/customers/${company}/${invoice.cust_num}`}>
                  {customer?.cust_id || `#${invoice.cust_num}`}
                  {customer?.name ? ` - ${customer.name}` : ''}
                </Link>
              </>
            )}
            {invoice.po_num ? ` · PO ${invoice.po_num}` : ''}
            {invoice.currency_code ? ` · ${invoice.currency_code}` : ''}
          </p>
          {originalInvoice && (
            <p className="oc-blurb">
              Reverses{' '}
              <Link to={`/invoices/${company}/${originalInvoice.invoice_num}`}>
                invoice {originalInvoice.invoice_num}
              </Link>
            </p>
          )}
        </div>
      </header>

      <div className="oc-stats">
        <Stat value={fmtAmt(totals.lines_amt)} label="Lines" />
        <Stat value={fmtAmt(totals.discount_amt)} label="Discount" />
        <Stat value={fmtAmt(totals.misc_amt)} label="Charges" />
        {/* doc_invoice_amt is Epicor's own total and includes tax, so it will not
            equal lines - discount + charges. */}
        <Stat value={fmtAmt(totals.invoice_total_amt)} label="Total (inc. tax)" />
        <Stat value={fmtAmt(totals.balance_amt)} label="Balance" />
      </div>

      <div className="oc-frame oc-blueprint" style={{ marginTop: 24 }}>
        <Corners />
        <div className="oc-tabs" style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              className={tab === t.id ? 'oc-tab active' : 'oc-tab'}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'lines' && (
          <table className="oc-table">
            <thead>
              <tr>
                <th>Line</th>
                <th>SKU</th>
                <th>Qty</th>
                <th>List</th>
                <th>Disc</th>
                <th>Net unit</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => (
                <tr key={l.invoice_line}>
                  <td>{l.invoice_line}</td>
                  <td>
                    <strong>
                      <PartCell partNum={l.part_num} pimSku={l.pim_sku} pimName={l.pim_name} />
                    </strong>
                    {l.line_desc ? <div className="oc-hint">{l.line_desc}</div> : null}
                  </td>
                  <td>{fmt(Number(l.selling_ship_qty))}</td>
                  <td>{fmtAmt(l.doc_list_price ?? l.doc_unit_price)}</td>
                  <td>{discountDisplay(l)}</td>
                  <td>{fmtAmt(netUnitPrice(l))}</td>
                  <td>{fmtAmt(netExt(l))}</td>
                </tr>
              ))}
              {lines.length === 0 && (
                <tr>
                  <td colSpan={7}>
                    <div className="oc-empty">
                      <p className="oc-empty-title">No line items staged for this invoice</p>
                      <p className="oc-empty-sub">Run a sales-orders sync to pull InvcDtl rows.</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}

        {tab === 'charges' && (
          <table className="oc-table">
            <thead>
              <tr>
                <th>Applies to</th>
                <th>Code</th>
                <th>Description</th>
                <th>Tax cat</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {misc.map((m) => (
                <tr key={`${m.invoice_line}-${m.seq_num}`}>
                  <td>{m.invoice_line ? `Line ${m.invoice_line}` : 'Invoice'}</td>
                  <td>{m.misc_code || '—'}</td>
                  <td>{m.description || '—'}</td>
                  <td>{m.tax_cat_id || '—'}</td>
                  <td>{miscAmountDisplay(m)}</td>
                </tr>
              ))}
              {misc.length === 0 && (
                <tr><td colSpan={5}>No freight or other charges on this invoice.</td></tr>
              )}
            </tbody>
          </table>
        )}

        {tab === 'orders' && (
          <table className="oc-table">
            <thead>
              <tr>
                <th>Order</th>
                <th>Date</th>
                <th>PO</th>
                <th>Ordered</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.order_num}>
                  <td>
                    <Link to={`/orders/${company}/${o.order_num}`}>{o.order_num}</Link>
                  </td>
                  <td>{fmtDate(o.order_date_effective || o.order_date)}</td>
                  <td>{o.po_num || '—'}</td>
                  <td>{fmtAmt(o.ordered_amt)}</td>
                </tr>
              ))}
              {orders.length === 0 && (
                <tr>
                  <td colSpan={4}>
                    Not linked to any sales order. Common for freight-only and
                    manually raised documents.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}

        {tab === 'payments' && (
          <table className="oc-table">
            <thead>
              <tr>
                <th>Head</th>
                <th>Date</th>
                <th>Ref</th>
                <th>Amount</th>
                <th>Posted</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((p) => (
                <tr key={`${p.group_id}-${p.head_num}`}>
                  <td>{p.group_id}/{p.head_num}</td>
                  <td>{fmtDate(p.tran_date)}</td>
                  <td>{p.check_ref || '—'}</td>
                  <td>{fmtAmt(p.doc_tran_amt)}</td>
                  <td>{p.posted ? 'Yes' : 'No'}</td>
                </tr>
              ))}
              {payments.length === 0 && <tr><td colSpan={5}>No payments applied.</td></tr>}
            </tbody>
          </table>
        )}

        {tab === 'credits' && (
          <>
            <table className="oc-table">
              <thead>
                <tr>
                  <th>Credit note</th>
                  <th>Date</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {creditNotes.map((c) => (
                  <tr key={c.invoice_num}>
                    <td>
                      <Link to={`/invoices/${company}/${c.invoice_num}`}>{c.invoice_num}</Link>
                    </td>
                    <td>{fmtDate(c.invoice_date)}</td>
                    <td>{fmtAmt(c.doc_invoice_amt)}</td>
                  </tr>
                ))}
                {creditNotes.length === 0 && (
                  <tr><td colSpan={3}>No credit notes reference this invoice.</td></tr>
                )}
              </tbody>
            </table>
            <p className="oc-hint" style={{ marginTop: 12 }}>
              Epicor only records the reversed invoice on a minority of credit notes, so
              an empty list here does not mean none exist. Search this customer in the
              invoice list, filtered to credit notes, to see all of them.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default InvoiceDetailPage;
